import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { YFormField } from '../YFormField';
import { YInput } from '../YInput';
import { YSelect } from '../YSelect';

function resolveInputType(field, values) {
  if (field.type === 'datetime' && values.allDay) return 'date';
  if (field.type === 'datetime') return 'datetime-local';
  if (field.type === 'date') return 'date';
  if (field.type === 'time') return 'time';
  if (field.type === 'number') return 'number';
  if (field.type === 'textarea') return 'textarea';
  return 'text';
}

function fieldVisible(field, values) {
  if (!field.when) return true;
  const { name, equals } = field.when;
  return values[name] === equals;
}

function renderField(field, { values, setValue, idPrefix, fieldMins = {} }) {
  if (field.slot) return null;
  if (!fieldVisible(field, values)) return null;

  const id = `${idPrefix}-${field.name}`;
  const value = values[field.name] ?? '';
  const onChange = (e) => setValue(field.name, e.target.value);

  if (field.type === 'switch') {
    return (
      <div key={field.name} className="y-dialog__switch">
        <Form.Check
          type="switch"
          id={id}
          label={field.label}
          checked={!!values[field.name]}
          onChange={(e) => setValue(field.name, e.target.checked)}
        />
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <YFormField
        key={field.name}
        label={field.label}
        htmlFor={id}
        required={field.required}
        labelClassName="y-label--dialog"
      >
        <YSelect id={id} value={value} onChange={onChange} aria-label={field.label}>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </YSelect>
      </YFormField>
    );
  }

  const inputType = resolveInputType(field, values);

  return (
    <YFormField
      key={field.name}
      label={field.label}
      htmlFor={id}
      required={field.required}
      className="y-dialog__field"
      labelClassName="y-label--dialog"
    >
      <YInput
        id={id}
        type={inputType}
        rows={field.rows}
        value={value}
        onChange={onChange}
        placeholder={field.placeholder}
        required={field.required}
        aria-label={field.label}
        min={fieldMins[field.name] ?? field.min}
        max={field.max}
      />
    </YFormField>
  );
}

/**
 * Renders dialog fields from a parsed schema. Use `slots` for custom blocks (e.g. recurrence).
 */
export function DialogFieldsRenderer({
  schema,
  values,
  setValue,
  idPrefix = 'dialog',
  slots = {},
  fieldMins = {},
}) {
  const renderList = (fields) =>
    fields.map((field) => {
      if (field.slot) {
        const slotNode = slots[field.slot];
        return slotNode ? <React.Fragment key={`slot-${field.slot}`}>{slotNode}</React.Fragment> : null;
      }
      if (field.type === 'row') {
        const rowClass =
          field.columns === 2 ? 'y-dialog__row y-dialog__row--2' : 'y-dialog__row';
        return (
          <div key={`row-${field.fields.map((f) => f.name).join('-')}`} className={rowClass}>
            {field.fields.map((child) => renderField(child, { values, setValue, idPrefix, fieldMins }))}
          </div>
        );
      }
      return renderField(field, { values, setValue, idPrefix, fieldMins });
    });

  return <>{renderList(schema.fields)}</>;
}

DialogFieldsRenderer.propTypes = {
  schema: PropTypes.shape({
    fields: PropTypes.array.isRequired,
  }).isRequired,
  values: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  idPrefix: PropTypes.string,
  slots: PropTypes.object,
  fieldMins: PropTypes.object,
};
